<template>
	<div class="line-chart-container">
		<LineChartGenerator
			:chart-options="enhancedChartOptions"
			:chart-data="paginatedChartData"
			:chart-id="chartId"
			:dataset-id-key="datasetIdKey"
			:plugins="plugins"
			:css-classes="cssClasses"
			:styles="styles"
			:width="width"
			:height="height"
		/>
		<!-- Legend Pagination Controls -->
		<div
			v-if="showPaginationControls"
			class="legend-pagination-controls mt-2"
		>
			<div class="d-flex align-center justify-space-between">
				<v-btn
					:disabled="currentLegendPage === 0"
					small
					text
					@click="previousLegendPage"
				>
					<v-icon left>
						mdi-chevron-left
					</v-icon>
					Previous
				</v-btn>
				<span class="legend-page-info">
					Showing {{ currentPageStart + 1 }}-{{ currentPageEnd }} of {{ totalDatasets }} datasets
				</span>
				<v-btn
					:disabled="currentLegendPage >= totalLegendPages - 1"
					small
					text
					@click="nextLegendPage"
				>
					Next
					<v-icon right>
						mdi-chevron-right
					</v-icon>
				</v-btn>
			</div>
		</div>
	</div>
</template>

<script>
import { Line as LineChartGenerator } from 'vue-chartjs/legacy'

import {
	Chart as ChartJS,
	Title,
	Tooltip,
	LineElement,
	LinearScale,
	CategoryScale,
	TimeScale,
	PointElement
} from 'chart.js'
import 'chartjs-adapter-date-fns'
import { getChartColorByIndex } from '../../utils/getChartColorByIndex.js'

ChartJS.register(
	Title,
	Tooltip,
	LineElement,
	LinearScale,
	CategoryScale,
	TimeScale,
	PointElement
)

export default {
	name: 'line-chart',
	components: {
		LineChartGenerator
	},
	props: {
		chartId: {
			type: String,
			default: 'line-chart'
		},
		datasetIdKey: {
			type: String,
			default: 'label'
		},
		width: {
			type: Number,
			default: 400
		},
		height: {
			type: Number,
			default: 400
		},
		cssClasses: {
			default: '',
			type: String
		},
		styles: {
			type: Object,
			default: () => {}
		},
		plugins: {
			type: Array,
			default: () => []
		},
		chartData: {
			type: Object
		},
		chartOptions: {
			type: Object
		},
		palette: {
			type: String,
			default: 'default'
		},
		colorVersion: {
			type: Number,
			default: 0
		},
		legendItemsPerPage: {
			type: Number,
			default: 5
		},
		useTimeScale: {
			type: Boolean,
			default: false
		},
		timeUnit: {
			type: String,
			default: 'day' // 'day', 'week', 'month', 'year'
		}
	},
	data () {
		return {
			currentLegendPage: 0
		}
	},
	computed: {
		totalDatasets () {
			return this.chartData?.datasets?.length || 0
		},
		showPaginationControls () {
			return this.totalDatasets > this.legendItemsPerPage
		},
		totalLegendPages () {
			return Math.ceil(this.totalDatasets / this.legendItemsPerPage)
		},
		currentPageStart () {
			return this.currentLegendPage * this.legendItemsPerPage
		},
		currentPageEnd () {
			return Math.min(this.currentPageStart + this.legendItemsPerPage, this.totalDatasets)
		},
		sortedDatasets () {
			if (!this.chartData?.datasets) return []

			// Calculate average values for each dataset to determine "top" results
			const datasetsWithAverage = this.chartData.datasets.map((dataset, index) => {
				const total = dataset.data.reduce((sum, value) => sum + (value || 0), 0)
				const average = total / dataset.data.length
				return {
					...dataset,
					originalIndex: index,
					averageValue: average,
					totalValue: total
				}
			})

			// Sort by total value descending (top results first)
			return datasetsWithAverage.sort((a, b) => b.totalValue - a.totalValue)
		},
		paginatedChartData () {
			// Reference colorVersion to trigger reactivity when custom colours change
			// eslint-disable-next-line no-unused-vars
			const _version = this.colorVersion

			if (!this.chartData) return null

			let sourceDatasets = this.chartData.datasets || []

			// Apply pagination if needed
			if (this.showPaginationControls) {
				const startIndex = this.currentPageStart
				const endIndex = this.currentPageEnd
				sourceDatasets = this.sortedDatasets.slice(startIndex, endIndex)
			}

			// Apply colors in exact order from chart-colors.js palette
			// Use the global index to continue cycling colors across pagination
			const enhancedDatasets = sourceDatasets.map((dataset, pageIndex) => {
				const newDataset = { ...dataset }
				// Calculate global index: start of current page + position within page
				const globalIndex = this.showPaginationControls
					? (this.currentPageStart + pageIndex)
					: pageIndex
				const color = getChartColorByIndex(globalIndex, this.palette)

				// Apply color to dataset
				newDataset.backgroundColor = color
				newDataset.borderColor = color

				return newDataset
			})

			return {
				...this.chartData,
				datasets: enhancedDatasets
			}
		},
		enhancedChartOptions () {
			const hasLongLabels = this.hasLongCategoryNames()
			const datasetCount = this.paginatedChartData?.datasets?.length || 0
			const isDark = this.$vuetify?.theme?.dark || false
			const gridColor = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
			const tickColor = isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'

			const baseOptions = {
				responsive: true,
				maintainAspectRatio: false,
				layout: {
					padding: {
						top: hasLongLabels && datasetCount > 8 ? 20 : 10,
						right: 10,
						bottom: 10,
						left: 10
					}
				},
				interaction: {
					intersect: false,
					mode: 'index'
				},
				scales: {
					x: this.getXAxisConfig(tickColor, gridColor),
					y: {
						beginAtZero: true,
						ticks: {
							maxTicksLimit: 6, // Reduced for cleaner look
							color: tickColor,
							font: {
								size: 11
							}
						},
						grid: {
							display: true,
							color: gridColor,
							drawBorder: false
						},
						border: {
							display: false
						}
					}
				},
				plugins: {
					legend: this.getLegendConfig(datasetCount, hasLongLabels),
					tooltip: {
						backgroundColor: 'rgba(0, 0, 0, 0.9)',
						titleColor: 'white',
						bodyColor: 'white',
						borderColor: 'rgba(255, 255, 255, 0.2)',
						borderWidth: 1,
						cornerRadius: 6,
						displayColors: true,
						usePointStyle: true,
						filter: function (tooltipItem) {
							// Only show non-zero values in tooltip to reduce clutter
							return tooltipItem.parsed.y !== 0
						}
					}
				},
				elements: {
					point: {
						radius: datasetCount > 10 ? 2 : 3, // Smaller points for many datasets
						hoverRadius: datasetCount > 10 ? 4 : 6,
						hitRadius: 8,
						borderWidth: 1
					},
					line: {
						borderWidth: datasetCount > 10 ? 1.5 : 2, // Thinner lines for many datasets
						tension: 0.1
					}
				}
			}

			// Deep merge with user-provided options
			return this.deepMerge(baseOptions, this.chartOptions || {})
		}
	},
	created () {
		// Set global font defaults for Chart.js
		ChartJS.defaults.font.family = 'Inter, sans-serif'
		ChartJS.defaults.font.size = 12
		ChartJS.defaults.font.weight = '400'

		// Set better defaults for line charts to handle dense data
		ChartJS.defaults.elements.point.radius = 3
		ChartJS.defaults.elements.point.hoverRadius = 5
		ChartJS.defaults.elements.line.tension = 0.1
	},
	methods: {
		getXAxisConfig (tickColor, gridColor) {
			if (this.useTimeScale) {
				return {
					type: 'time',
					time: {
						unit: this.timeUnit,
						displayFormats: {
							day: 'dd MMM',
							week: 'dd MMM',
							month: 'MMM yyyy',
							year: 'yyyy'
						},
						tooltipFormat: 'dd MMM yyyy'
					},
					ticks: {
						maxTicksLimit: 6,
						color: tickColor,
						font: {
							size: 11
						}
					},
					grid: {
						display: true,
						color: gridColor
					}
				}
			}
			return {
				ticks: {
					maxTicksLimit: 6,
					maxRotation: 45,
					minRotation: 0,
					color: tickColor,
					font: {
						size: 11
					}
				},
				grid: {
					display: true,
					color: gridColor
				}
			}
		},
		hasLongCategoryNames () {
			if (!this.paginatedChartData?.datasets) return false
			return this.paginatedChartData.datasets.some(dataset =>
				dataset.label && dataset.label.length > 25
			)
		},
		nextLegendPage () {
			if (this.currentLegendPage < this.totalLegendPages - 1) {
				this.currentLegendPage++
			}
		},
		previousLegendPage () {
			if (this.currentLegendPage > 0) {
				this.currentLegendPage--
			}
		},
		getLegendConfig (datasetCount, hasLongLabels) {
			const isDark = this.$vuetify?.theme?.dark || false
			const legendColor = isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)'
			// Always show legend since we're paginating
			return {
				position: 'bottom',
				labels: {
					usePointStyle: true,
					pointStyle: 'circle',
					padding: 12,
					color: legendColor,
					font: {
						size: 11
					},
					boxWidth: 10,
					boxHeight: 10
				}
			}
		},
		deepMerge (target, source) {
			const result = { ...target }
			for (const key in source) {
				if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
					result[key] = this.deepMerge(result[key] || {}, source[key])
				} else {
					result[key] = source[key]
				}
			}
			return result
		}
	}
}
</script>

<style scoped>
.line-chart-container {
	width: 100%;
}

.legend-pagination-controls {
	border-top: 1px solid var(--global-border-color);
	padding-top: 8px;
}

.legend-page-info {
	font-size: 12px;
	color: var(--global-foreground);
	font-weight: 500;
}
</style>
